import { useEffect, useState } from "react";

import type { JackpotCampaign } from "@/campaigns/modules/jackpot/types";
import { clockCadenceMs } from "@/campaigns/modules/jackpot/types";

/**
 * The board's clock: `now`, kept current at the cadence the board actually needs.
 *
 * The pot, the deadline and the last payout change only when the progress stream pushes, and the
 * store re-derives the view for those. What changes on its own is the countdown on a timed pot,
 * the end of a cooldown, and a "just won" expiring — all wall-clock. Those have to live in a hook,
 * not in `assembleJackpots`: putting `Date.now()` into assembly would defeat the store's
 * `listEqual` short-circuit and re-render every subscriber on every push.
 *
 * Cadence follows what is on screen (the same reasoning as `useTimeRemaining`): per second only
 * inside the final hour of the nearest boundary, per minute above that, and off entirely when
 * nothing on the board depends on time — a board of progressives with no recent win never ticks.
 * Rescheduling after each tick (rather than a fixed interval) is what lets a board switch itself
 * from per-minute to per-second as a deadline approaches.
 */
export function useJackpotClock(jc: JackpotCampaign): number {
  const [now, setNow] = useState(() => Date.now());
  const cadence = clockCadenceMs(jc, now);
  // Re-arm when the set of boundaries changes (a push moved a deadline or queued a slot), not on
  // every render: the campaign key + a fingerprint of the time-bearing fields is enough.
  const boundaryKey = jc.jackpots
    .map(
      (v) =>
        `${v.jackpot.id}:${v.events?.active?.payout_before ?? ""}:${v.events?.next?.activated ?? ""}:${v.events?.last?.payout_date ?? ""}`
    )
    .join("|");

  useEffect(() => {
    if (cadence === null) return;
    let id: number | undefined;
    const schedule = () => {
      const next = clockCadenceMs(jc, Date.now());
      if (next === null) return;
      id = window.setTimeout(() => {
        setNow(Date.now());
        schedule();
      }, next);
    };
    schedule();
    return () => window.clearTimeout(id);
    // `jc` is intentionally not a dependency: the boundary fingerprint is the only time-relevant
    // input, and stream pushes already re-render the caller with a fresh `jc`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryKey, jc.campaign.status, cadence === null]);

  return now;
}
